import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import useI18n from 'hooks/useI18n'
import { ReadObject } from 'services/storage'
import { ChangeRequiredPassword, SelectEntryArea } from 'services/unifiedLogin'
import { IDENTITY_AREA_SESSIONS } from 'services/identity'
import { passwordChangeRequired } from 'services/profile'
import { DoLogout } from 'services/authentication'
import { exposeStrapiError } from 'utils'

export default function useController(area) {
  const { t } = useI18n()
  const history = useHistory()
  const formRef = useRef()
  const busy = useRef(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState(false)
  const formItems = useMemo(() => [
    { ref: 'password', id: 'change-new-password', label: t('new_password'), type: 'password', required: true, full: true },
    { ref: 'passwordConfirmation', id: 'change-confirm-password', label: t('confirm_new_password'), type: 'password', required: true, full: true },
  ].map(item => ({ ...item, placeholder: item.label })), [t])
  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        if (!ReadObject(IDENTITY_AREA_SESSIONS[area])?.jwt) { history.replace('/login'); return }
        const destination = await SelectEntryArea(area)
        if (!active) return
        if (!destination) { setError(true); return }
        if (!passwordChangeRequired(ReadObject(IDENTITY_AREA_SESSIONS[area])?.user)) { history.replace(destination); return }
        setReady(true)
      } catch { if (active) setError(true) }
      finally { if (active) setLoading(false) }
    }
    load()
    return () => { active = false }
  }, [area, history])
  const save = async () => {
    if (busy.current || !ready) return
    const form = formRef.current?.getForm()
    if (!form) return
    if (form.password !== form.passwordConfirmation || form.password.length < 8) {
      setError(true); return
    }
    busy.current = true
    setSaving(true)
    setError(false)
    try {
      const response = await ChangeRequiredPassword(area, form)
      if (response && exposeStrapiError(response)) return
      if (response?.jwt) history.replace(await SelectEntryArea(area) || '/choose-area')
      else setError(true)
    } catch { setError(true) }
    finally { busy.current = false; setSaving(false) }
  }
  const logout = async () => { await DoLogout(); history.replace('/login') }
  return { t, formRef, formItems, loading, saving, ready, error, save, logout }
}
